
import Vue from "vue";
import { Component, Inject, Prop } from "vue-property-decorator";
import {
  Control,
  ControlConditions,
  ControlType,
  CONTROL_TYPES,
  RangeOptions,
  DropdownOptions,
  ButtonBarOrDropdownItem,
  CodeOptionsV,
  ShadowOptionsV,
  ShadowOptions,
  CodeOptions,
  DatePickerOptionsV,
  textAndRichTextVariableItem,
  ControlOptions,
  BreakpointOptions,
  LayoutType,
  controlLayoutTypes
} from "@/types/controls";
import { ElementStudioContext } from "./ElementStudioTabsAndPanels.vue";
import defensiveCopy from "@/util/defensive-copy";
import draggable from "vuedraggable";
import Accordion from "@/components/UI/Accordion.vue";
import Repeater from "@/components/Reusable/CustomInputs/Repeater/Repeater.vue";
import ShittyTextInput from "@/components/Reusable/CustomInputs/ShittyTextInput.vue";
import CopyButton from "@/components/Reusable/CopyButton.vue";
import TwigCodeHelper from "./TwigCodeHelper.vue";
import ElementStudioConditionInput from "./ElementStudioConditionInput.vue";
import ElementStudioUnitOptions from "./ElementStudioUnitOptions.vue";

import lodash from "lodash";
import { getHash } from "@/util/hash";
import { configStore, uiStore } from "@/store/store";
import { UnitOptions } from "@/types/unit";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import ElementStudioRangeOptions from "@/components/ElementStudio/ElementStudioRangeOptions.vue";
import ElementStudioRepeaterOptions from "@/components/ElementStudio/ElementStudioRepeaterOptions.vue";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import ElementStudioDropdownOptions from "@/components/ElementStudio/ElementStudioDropdownOptions.vue";
import ElementStudioBreakpointOptions from "@/components/ElementStudio/ElementStudioBreakpointOptions.vue";
import ElementStudioVariableOptions from "@/components/ElementStudio/ElementStudioVariableOptions.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import {
  getDynamicPathAcceptsItems,
  getPathWithoutRepeaterIndex
} from "@/lib/dynamic-data/utils";
import Copyable from "@/components/ElementStudio/Copyable.vue";
import {
  doesControlTypeAcceptChildren,
  isControlTypeARepeater
} from "@/lib/elements/utils";
import ElementStudioMediaOptions from "@/components/ElementStudio/ElementStudioMediaOptions.vue";
import ElementStudioMediaSizeOptions from "@/components/ElementStudio/ElementStudioMediaSizeOptions.vue";
import ElementStudioIconOptions from "@/components/ElementStudio/ElementStudioIconOptions.vue";
import Icon from "@/components/UI/Icon.vue";
import { spacingBarsPresetSlug } from "@/lib/elements/constants";
import { getSpacingBarsFromPresetSlug } from "@/lib/elements/studio/spacing-bars";
import { forceNextTick } from "@/util/force-next-tick";
import ElementStudioPostChooserOptions from "@/components/ElementStudio/ElementStudioPostChooserOptions.vue";
import { jsonStringifyOrUndefined } from "@/util/json-stringify-or-undefined";
import ElementStudioIconsAutocomplete from "@/components/ElementStudio/ElementStudioIconsAutocomplete.vue";
import InlineRepeater from "@/components/Reusable/CustomInputs/InlineRepeater.vue";
import IconButton from "@/components/UI/IconButton.vue";
import { isNestingRuleAllowedInside } from "@/lib/tree/nesting";
import ElementStudioVideoOptions from "@/components/ElementStudio/ElementStudioVideoOptions.vue";
import ElementStudioAlertBoxOptions from "@/components/ElementStudio/ElementStudioAlertBoxOptions.vue";
import ElementStudioFocusPointOptions from "@/components/ElementStudio/ElementStudioFocusPointOptions.vue";
import { DynamicFieldReturnType } from "@/types/dynamic-property-path";
import { slugToPhpSlug } from "@/lib/elements/studio/element-to-php";
import ElementStudioSaveNewDialog from "@/components/ElementStudio/ElementStudioSaveNewDialog.vue";
import { ElementStudioSaveLocationWithName } from "@/types/element-studio";
import slugify from "slugify";
import { logNotificationOfSameItem } from "@/lib/elements/studio/misc";
import {
  getSlugFromNamespaceAndFilename,
  savePresetToServer
} from "@/lib/elements/studio/presets";
import { pipe } from "fp-ts/function";
import { fold } from "fp-ts/Either";
import NotificationLogger from "@/lib/notifications/notifications";
import ElementStudioHideControlInput from "@/components/ElementStudio/ElementStudioHideControlInput.vue";
import { SubscriptionMode, subscriptionMode } from "@/types/load";

@Component({
  name: "ControlSectionOrControl",
  components: {
    ElementStudioHideControlInput,
    ElementStudioSaveNewDialog,
    ElementStudioFocusPointOptions,
    ElementStudioVideoOptions,
    ElementStudioIconsAutocomplete,
    IconButton,
    InlineRepeater,
    ElementStudioBreakpointOptions,
    Icon,
    ElementStudioIconOptions,
    ElementStudioMediaSizeOptions,
    ElementStudioMediaOptions,
    ElementStudioPostChooserOptions,
    TextInput,
    Copyable,
    ElementStudioDropdownOptions,
    ElementStudioVariableOptions,
    DropdownInput,
    ElementStudioRepeaterOptions,
    ElementStudioRangeOptions,
    Dropdown,
    CopyButton,
    ShittyTextInput,
    Repeater,
    ControlSectionOrControl,
    draggable,
    Accordion,
    TwigCodeHelper,
    ElementStudioConditionInput,
    ElementStudioUnitOptions,
    ElementStudioAlertBoxOptions
  }
})
export default class ControlSectionOrControl extends Vue {
  @Prop()
  path!: string;

  @Prop()
  parentPath!: string;

  @Prop()
  index!: number;

  @Prop()
  value!: Control;

  @Prop()
  twigParentPath!: string;

  @Prop()
  parentControl!: Control | undefined;

  codeHelpOpen = false;
  showContentEditableCode = false;
  showNewPresetDialog = false;

  // can't have media queries within media queries
  get canHaveMediaQueries() {
    return !!this.parentControl?.enableMediaQueries;
  }

  get parentType() {
    return this.parentControl?.options.type;
  }

  get keyToRerenderCodeHelp() {
    const json = jsonStringifyOrUndefined(this.value);
    return getHash(`${json}`);
  }

  accordionState: Array<undefined | boolean | null> = [false];

  get isPresetSection() {
    return !!this.presetSectionSlug;
  }

  get presetSectionSlug() {
    return this.value.options.sectionOptions?.preset?.slug;
  }

  get isSection() {
    return this.value.options.type === "section";
  }

  get isRepeater() {
    return isControlTypeARepeater(this.value.options.type);
  }

  get isParentARepeater() {
    return isControlTypeARepeater(this.parentType);
  }

  get controlAcceptsChildren() {
    return doesControlTypeAcceptChildren(this.value.options.type);
  }

  get isInContentTab() {
    return this.path.startsWith("contentSections");
  }

  get spacingBarPresetSlug() {
    if (!this.isSection) return undefined;

    return spacingBarsPresetSlug.find(
      slug => slug === this.value.options?.sectionOptions?.preset?.slug
    );
  }

  get classList() {
    return {
      "element-studio-control-or-control-section": true,
      "element-studio-control-section": true,
      "element-studio-control": !this.controlAcceptsChildren
    };
  }

  get dynamicPropertyPath() {
    const dynamicPropertyPaths = this.context.element().dynamicPropertyPaths;

    if (!dynamicPropertyPaths) return false;

    const dynPropPathThatMatchesTwigPath = dynamicPropertyPaths.find(
      dynPropPath =>
        getPathWithoutRepeaterIndex(dynPropPath.path) ===
        this.twigPathWithoutRepeaterIndex
    );

    return dynPropPathThatMatchesTwigPath;
  }

  get twigPathWithoutRepeaterIndex() {
    return getPathWithoutRepeaterIndex(
      `${this.twigParentPath}${this.isParentARepeater ? "[]" : ""}.${
        this.value.slug
      }`
    );
  }

  get dynamicPathAcceptsItems() {
    return getDynamicPathAcceptsItems();
  }

  get contentEditableHelperCode() {
    return `// Add to the control value's wrapper HTML to make it ContentEditable

    ${this.twigPathForContentEditable}
    `;
  }

  get twigPathForContentEditable() {
    if (this.isRepeater || this.isParentARepeater) {
      return `{{ dataContentEditablePropertyPath('${this.twigParentPath}['~ loop.index0 ~ '].your_control') }}`;
    }

    return `{{ dataContentEditablePropertyPath('${this.twigPathWithoutRepeaterIndex}') }}`;
  }

  get datePickerOptions() {
    return Object.keys(DatePickerOptionsV.props);
  }

  get elementStudioData() {
    return this.context.getElementStudioData();
  }

  async saveNewPreset(newPreset: ElementStudioSaveLocationWithName) {
    if (!this.isSection) return;

    const filename = slugify(newPreset.name);
    const newSlug = getSlugFromNamespaceAndFilename(
      newPreset.namespace,
      filename
    );

    if (
      this.elementStudioData.presets.find(preset => preset.slug === newSlug)
    ) {
      logNotificationOfSameItem(
        this.elementStudioData.saveLocations,
        newPreset,
        "preset"
      );

      return;
    }

    const presetSectionFromCurrentSection = this.value;

    presetSectionFromCurrentSection.options.sectionOptions = {
      ...presetSectionFromCurrentSection.options.sectionOptions,
      preset: {
        slug: newSlug
      }
    };

    pipe(
      await savePresetToServer(
        {
          slug: newSlug,
          section: presetSectionFromCurrentSection
        },
        newPreset.directoryPath,
        filename,
        newSlug
      ),
      fold(
        e => {
          console.error(e);
          NotificationLogger.log.message(
            "Failed to save element, check console"
          );
        },
        () => {
          const newData = defensiveCopy(this.elementStudioData);

          newData.presets.push({
            slug: newSlug,
            directoryPath: newPreset.directoryPath
          });

          newData.presetSections.push({
            slug: newSlug,
            section: presetSectionFromCurrentSection
          });

          this.context.updateElementStudioData(newData);

          // Replace current control with the newly created preset
          this.context.updateControl(
            this.parentPath,
            this.index,
            presetSectionFromCurrentSection
          );
        }
      )
    );
  }

  toggleAccordion(index: number) {
    /*
    Vue cannot make a value reactive when you directly set an item with the index,
    e.g. vm.items[indexOfItem] = newValue
    Therefore, use Vue.set
    */
    Vue.set(this.accordionState, index, !this.accordionState[index]);
  }

  @Inject("context") readonly context!: ElementStudioContext;

  handleDrag(newControls: Control[]) {
    this.context.replaceControls(`${this.path}.children`, newControls);
  }

  get canPaste() {
    return (
      this.controlAcceptsChildren &&
      !this.isPresetSection &&
      uiStore.clipboard.elementStudio &&
      uiStore.clipboard.elementStudio.copiedControl
    );
  }

  get canReplace() {
    if (
      !uiStore.clipboard.elementStudio ||
      !uiStore.clipboard.elementStudio.copiedControl
    ) {
      return false;
    }

    const controlToPaste = uiStore.clipboard.elementStudio.copiedControl;

    // Only allow to replace sections with other sections.
    // In a preset, there must always be a section at the top
    if (this.isSection && controlToPaste.options.type !== "section") {
      return false;
    }

    return true;
  }

  handlePaste() {
    if (!this.controlAcceptsChildren) return;

    if (!uiStore.clipboard.elementStudio) return;

    const controlToPaste = uiStore.clipboard.elementStudio.copiedControl;

    if (!controlToPaste) return;

    this.context.pasteControl(`${this.path}.children`, controlToPaste);
  }

  handleReplace() {
    if (!uiStore.clipboard.elementStudio) return;

    const controlToPaste = uiStore.clipboard.elementStudio.copiedControl;

    if (!controlToPaste) return;

    this.context.updateControl(this.parentPath, this.index, controlToPaste);
  }

  handleMakePreset() {
    this.showNewPresetDialog = true;
  }

  get controlTypes() {
    const capitalize = (s: string) => {
      return s.charAt(0).toUpperCase() + s.slice(1);
    };

    const disallowedInContent: ControlType[] = [
      // "spacing_complex",
      // "gradient",
      // "unit",
      // "color",
      // "breakpoint_dropdown",
      // "border_complex",
      // "shadow"
    ];

    return CONTROL_TYPES.sort()
      .filter(this.removeDisallowedControlTypes)
      .map(c => {
        return {
          text: capitalize(c),
          value: c
        };
      })
      .map(x => {
        if (this.isInContentTab && disallowedInContent.includes(x.value)) {
          return {
            ...x,
            disabled: true
          };
        } else {
          return x;
        }
      });
  }

  copy() {
    const copiedControl = defensiveCopy(this.value);
    uiStore.copyElementStudioControl(copiedControl);
  }

  get availablePresetSections() {
    return this.context.getPresetSections().map(preset => {
      return {
        text: preset.slug,
        value: preset.slug
      };
    });
  }

  updatePresetSectionSlug(newPresetSectionSlug: string) {
    const newPresetSection = defensiveCopy(
      this.context.getPresetSection(newPresetSectionSlug)
    );

    if (newPresetSection !== undefined) {
      newPresetSection.section.options.sectionOptions = {
        ...newPresetSection.section.options.sectionOptions,
        type: "popout"
      };

      this.context.updateControl(
        this.parentPath,
        this.index,
        newPresetSection.section
      );

      this.turnRelativePropPathsToWhitelistInFlatPropsIntoAbsolutePathsAndUpdateElement(
        newPresetSection.section.options.sectionOptions,
        newPresetSection.section.slug
      );
      this.turnRelativeDynamicPropPathsIntoAbsoluteAndUpdateElement(
        newPresetSection.section.options.sectionOptions,
        newPresetSection.section.slug
      );
    }
  }

  turnRelativePropPathsToWhitelistInFlatPropsIntoAbsolutePathsAndUpdateElement(
    sectionOptions: ControlOptions["sectionOptions"],
    newControlSlugArg: string
  ) {
    if (
      !sectionOptions ||
      !sectionOptions.preset?.relativePropertyPathsToWhitelistInFlatProps
    ) {
      return;
    }

    const newControlSlug =
      newControlSlugArg || slugToPhpSlug(newControlSlugArg);

    let newPropertyPathsToWhitelistInFlatProps = defensiveCopy(
      this.context.element().propertyPathsToWhitelistInFlatProps
    );

    const absolutePathToWhitelistInFlatProps = sectionOptions.preset.relativePropertyPathsToWhitelistInFlatProps.map(
      relativePath => `${this.twigParentPath}.${newControlSlug}.${relativePath}`
    );

    // when changing the slug, we don't want to leave the old paths
    const oldAbsolutePathToWhitelistInFlatProps = sectionOptions.preset.relativePropertyPathsToWhitelistInFlatProps.map(
      relativePath =>
        `${this.twigParentPath}.${this.value.slug}.${relativePath}`
    );

    if (newPropertyPathsToWhitelistInFlatProps) {
      // prevent adding the same path multiple times
      newPropertyPathsToWhitelistInFlatProps = newPropertyPathsToWhitelistInFlatProps.filter(
        path => {
          return (
            !absolutePathToWhitelistInFlatProps.includes(path) &&
            !oldAbsolutePathToWhitelistInFlatProps.includes(path)
          );
        }
      );

      newPropertyPathsToWhitelistInFlatProps = [
        ...newPropertyPathsToWhitelistInFlatProps,
        ...absolutePathToWhitelistInFlatProps
      ];
    } else {
      newPropertyPathsToWhitelistInFlatProps = absolutePathToWhitelistInFlatProps;
    }

    // this func is called right after updating a control. Wait until the changes propagate before updating again
    forceNextTick(() =>
      this.context.updateElementValue(
        "propertyPathsToWhitelistInFlatProps",
        newPropertyPathsToWhitelistInFlatProps
      )
    );
  }

  turnRelativeDynamicPropPathsIntoAbsoluteAndUpdateElement(
    sectionOptions: ControlOptions["sectionOptions"],
    newControlSlugArg: string
  ) {
    if (
      !sectionOptions ||
      !sectionOptions.preset?.relativeDynamicPropertyPaths
    ) {
      return;
    }
    const newControlSlug =
      newControlSlugArg || slugToPhpSlug(newControlSlugArg);

    let newDynamicPropertyPaths = defensiveCopy(
      this.context.element().dynamicPropertyPaths
    );

    const absoluteDynamicPropPaths = sectionOptions.preset.relativeDynamicPropertyPaths.map(
      relativeDynamicPropertyPath => ({
        ...relativeDynamicPropertyPath,
        path: `${this.twigParentPath}.${newControlSlug}.${relativeDynamicPropertyPath.path}`
      })
    );

    // when changing the slug, we don't want to leave the old paths
    const oldAbsoluteDynamicPropPaths = sectionOptions.preset.relativeDynamicPropertyPaths.map(
      relativeDynamicPropertyPath => ({
        ...relativeDynamicPropertyPath,
        path: `${this.twigParentPath}.${this.value.slug}.${relativeDynamicPropertyPath.path}`
      })
    );

    if (newDynamicPropertyPaths) {
      // prevent adding the same path multiple times
      newDynamicPropertyPaths = newDynamicPropertyPaths.filter(
        newDynPropPath => {
          return (
            !absoluteDynamicPropPaths.some(
              dynPropPath => dynPropPath.path === newDynPropPath.path
            ) &&
            !oldAbsoluteDynamicPropPaths.some(
              dynPropPath => dynPropPath.path === newDynPropPath.path
            )
          );
        }
      );

      newDynamicPropertyPaths = [
        ...newDynamicPropertyPaths,
        ...absoluteDynamicPropPaths
      ];
    } else {
      newDynamicPropertyPaths = absoluteDynamicPropPaths;
    }

    // this func is called right after updating a control. Wait until the changes propagate before updating again
    forceNextTick(() =>
      this.context.updateElementValue(
        "dynamicPropertyPaths",
        newDynamicPropertyPaths
      )
    );
  }

  removeDisallowedControlTypes(controlType: ControlType) {
    const disallowedControlTypes: ControlType[] = [
      "class",
      // "condition", // TODO: what could element devs create if we allowed the condition control? Probably some cool stuff
      "font_family",
      "global_colors",
      "message"
    ];

    return !disallowedControlTypes.includes(controlType);
  }

  addControl() {
    this.context.addControl(this.path);
  }

  deleteControl() {
    this.context.deleteControl(this.parentPath, this.index);
  }

  updateControlType(newType: ControlType) {
    const newControl = defensiveCopy(this.value);
    newControl.options.type = newType;

    if (!this.isInContentTab) {
      // if we are in the content tab all controls already have a vertical layout
      const controlTypesThatShouldHaveVerticalLayout: ControlType[] = [
        "code",
        "repeater",
        "inline_repeater",
        "spacing_complex",
        "border_complex",
        "gradient",
        "icon",
        "richtext",
        "shadow",
        "wpmedia",
        "text",
        "focus_point",
        "url"
      ];

      if (controlTypesThatShouldHaveVerticalLayout.includes(newType)) {
        newControl.options.layout = "vertical";
      } else {
        newControl.options.layout = "inline";
      }
    }

    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  updateVariableItems(newItems: textAndRichTextVariableItem[]) {
    const newControl = defensiveCopy(this.value);
    newControl.options.variableItems = newItems;
    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  updateVariableItem({
    newItem,
    index
  }: {
    newItem: textAndRichTextVariableItem;
    index: number;
  }) {
    const newControl = defensiveCopy(this.value);

    if (!newControl.options.variableItems) {
      newControl.options.variableItems = [];
    }

    newControl.options.variableItems[index] = newItem;

    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  updateDropdownItems(newItems: ButtonBarOrDropdownItem[]) {
    const newControl = defensiveCopy(this.value);
    newControl.options.items = newItems;
    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  updateDropdownItem({
    newItem,
    index
  }: {
    newItem: ButtonBarOrDropdownItem;
    index: number;
  }) {
    const newControl = defensiveCopy(this.value);

    if (!newControl.options.items) {
      newControl.options.items = [];
    }

    // @ts-ignore
    newControl.options.items[index] = newItem;

    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  updateOptions(
    newOptions:
      | UnitOptions
      | RangeOptions
      | DropdownOptions
      | BreakpointOptions,
    optionsPath: string
  ) {
    const newControl = defensiveCopy(this.value);
    const path = `options.${optionsPath}`;

    lodash.set(newControl, path, newOptions);
    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  get availableDescendantElements() {
    return Object.values(configStore.elements)
      .filter(element => {
        if (!element) return false;

        // direct children are added automatically in the control
        // And we don't want to add an element that isn't allowed inside
        if (element.nestingRule.restrictedToBeADirectChildOf) {
          return false;
        }

        return isNestingRuleAllowedInside(
          element.nestingRule,
          this.context.element().nestingRule,
          this.context.element().slug
        );
      })
      .map(element => element?.slug);
  }

  updateAddExtraElementsBySlug(newElementSlugs: string[]) {
    const newControl = defensiveCopy(this.value);

    newControl.options.addRegisteredChildrenOptions = {
      ...newControl.options.addRegisteredChildrenOptions,
      addExtraElementsBySlug: newElementSlugs
    };

    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  get autofocusSupported() {
    switch (this.value.options.type) {
      case "text":
        return true;
      case "video":
        return true;
      default:
        return false;
    }
  }

  get codeTypes() {
    /*
    return CodeOptionsV.props.language.types.map(type => type.value);
    that used to work but after we added the autofillOnEmpty property to CodeOptionsV
    it stopped working
    */
    return ['css', 'html', 'javascript', 'x-php'];
  }

  updateCodeType(language: CodeOptions["language"]) {
    const newControl = defensiveCopy(this.value);

    newControl.options.codeOptions = {
      ...newControl.options.codeOptions,
      language
    };

    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  get shadowTypes() {
    return ShadowOptionsV.props.type.types.map(type => type.value);
  }

  updateShadowType(type: ShadowOptions["type"]) {
    const newControl = defensiveCopy(this.value);
    newControl.options.shadowOptions = {
      ...newControl.options.shadowOptions,
      type
    };

    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  updateTextOptionsMultiline(multiline: boolean) {
    const newControl = defensiveCopy(this.value);
    newControl.options.textOptions = {
      ...newControl.options.textOptions,
      multiline: multiline
    };

    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  updateButtonBarOptionsSize(big: boolean) {
    const newControl = defensiveCopy(this.value);
    newControl.options.buttonBarOptions = {
      ...newControl.options.buttonBarOptions,
      size: big ? "big" : "small",
      layout: "default"
    };

    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  updateButtonBarOptionsLayout(multiline: boolean) {
    const newControl = defensiveCopy(this.value);
    newControl.options.buttonBarOptions = {
      ...newControl.options.buttonBarOptions,
      layout: multiline ? "multiline" : "default",
      size: "small"
    };

    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  addPreset() {
    this.context.addPreset(this.path);
  }

  get controlLayouts() {
    return ["inline", "vertical"];
  }

  updateControlLayout(newLayout: "inline" | "vertical" | undefined) {
    const newControl = defensiveCopy(this.value);
    newControl.options.layout = newLayout;
    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  updateKeywords(newKeywords: string | null) {
    const newControl = defensiveCopy(this.value);
    newControl.keywords =
      newKeywords === null ? undefined : newKeywords.split(",");
    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  get keywordsValue() {
    return this.value.keywords ? this.value.keywords.join(",") : "";
  }

  updatePlaceholder(newPlaceholder: string | null) {
    const newControl = defensiveCopy(this.value);
    newControl.options.placeholder = newPlaceholder ?? undefined;
    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  updateColorType(type: "solidOnly" | "solidAndGradient") {
    const newControl = defensiveCopy(this.value);

    newControl.options.colorOptions = {
      ...newControl.options.colorOptions,
      type
    };

    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  updateConditions(newConditions: ControlConditions) {
    const newControl = defensiveCopy(this.value);

    if (Array.isArray(newConditions) && !newConditions.length) {
      delete newControl.options.condition;
    } else {
      newControl.options.condition = newConditions;
    }

    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  updateHideForElements(newValue: string[]) {
    const newControl = defensiveCopy(this.value);
    newControl.options.hideForElements = newValue;
    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  deleteHideForElements() {
    const newControl = defensiveCopy(this.value);
    delete newControl.options.hideForElements;
    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  get sectionLayouts() {
    return controlLayoutTypes;
  }

  updateSectionLayoutType(newLayout: LayoutType = "accordion") {
    const newControl = defensiveCopy(this.value);
    newControl.options.sectionOptions = {
      ...newControl.options.sectionOptions,
      type: newLayout
    };
    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  /*
   * Updating on "focus out" because the "key" prop is the slug
   * So changing the label -> slug will re-render the component while the user is typing
   */
  updateLabelAndSlugOnFocusOut(event: { target: HTMLInputElement }) {
    const newLabel = event.target.value;
    const newControl = defensiveCopy(this.value);

    newControl.label = newLabel;
    newControl.slug =
      // replace anything non-alphanumeric with "_".
      // "Display: time (ms!)" becomes "display_time_ms_"
      newLabel.replace(/[^a-z0-9]+/gi, "_").toLowerCase();

    if (/^\d/.test(newControl.slug)) {
      // it starts with a number
      newControl.slug = `slug_cant_start_with_number_${newControl.slug}`;
    }

    this.context.updateControl(this.parentPath, this.index, newControl);

    // State that depends on the slug will be invalid after changing it
    this.updateElementPropertiesNotTiedToControl(newControl);
  }

  updateControlBoolean(
    enable: boolean,
    property: "enableHover" | "enableMediaQueries"
  ) {
    const newControl = defensiveCopy(this.value);
    newControl[property] = enable;
    this.context.updateControl(this.parentPath, this.index, newControl);
  }

  updateDynamicPropertyPaths(
    enable: boolean,
    accepts: DynamicFieldReturnType = "string",
    proOnly = false
  ) {
    let newDynamicPropertyPaths = defensiveCopy(
      this.context.element().dynamicPropertyPaths
    );

    // Avoids duplicating the element on each change
    if (newDynamicPropertyPaths) {
      newDynamicPropertyPaths = newDynamicPropertyPaths.filter(
        propPath => propPath.path !== this.twigPathWithoutRepeaterIndex
      );
    }

    if (enable) {
      if (!newDynamicPropertyPaths) {
        newDynamicPropertyPaths = [];
      }

      newDynamicPropertyPaths.push({
        accepts,
        path: this.twigPathWithoutRepeaterIndex,
        ...(proOnly && { proOnly })
      });
    }

    this.context.updateElementValue(
      "dynamicPropertyPaths",
      newDynamicPropertyPaths
    );
  }

  isSpacingBarEnabled() {
    const spacingBars = this.context.element().spacingBars;

    if (!spacingBars || !spacingBars.length) return false;

    if (!this.spacingBarPresetSlug) return false;

    const spacingBarsAffectedPropertyPaths = getSpacingBarsFromPresetSlug(
      this.spacingBarPresetSlug,
      this.twigPathWithoutRepeaterIndex
    ).map(spaceBar => spaceBar.affectedPropertyPath);

    // If any current spacing bar matches a spacingBarsAffectedPropertyPaths it's enabled
    const enabledSpaceBar = spacingBars.find(elementSpaceBar =>
      spacingBarsAffectedPropertyPaths.includes(
        elementSpaceBar.affectedPropertyPath
      )
    );

    return !!enabledSpaceBar;
  }

  updateSpacingBar(enabled: boolean) {
    if (!this.spacingBarPresetSlug) return;

    let newSpacingBars = defensiveCopy(this.context.element().spacingBars);

    const spacingBars = getSpacingBarsFromPresetSlug(
      this.spacingBarPresetSlug,
      this.twigPathWithoutRepeaterIndex
    );

    if (enabled) {
      if (!newSpacingBars) {
        newSpacingBars = [];
      }

      newSpacingBars = [...newSpacingBars, ...spacingBars];
    } else if (newSpacingBars) {
      const spacingBarsAffectedPropertyPaths = spacingBars.map(
        spaceBar => spaceBar.affectedPropertyPath
      );

      newSpacingBars = newSpacingBars.filter(
        elementSpaceBar =>
          !spacingBarsAffectedPropertyPaths.includes(
            elementSpaceBar.affectedPropertyPath
          )
      );
    }

    this.context.updateElementValue("spacingBars", newSpacingBars);
  }

  // must be called last
  updateElementPropertiesNotTiedToControl(newControl: Control) {
    // nextTick to wait for props to  propagate with the new values.
    // This avoids overriding changes since we call the functions one after another
    // The correct way would've been to update the entire element value through context and not just a single property
    // But I'm not refactoring everything
    forceNextTick(() => this.updateSpacingBar(false));
    forceNextTick(() => this.updateDynamicPropertyPaths(false));
    forceNextTick(() => this.updatePropertyPathsToWhitelistInFlatProps(false));
    forceNextTick(() =>
      this.updatePropertyPathsToSsrElementWhenValueChanges(false)
    );

    this.turnRelativePropPathsToWhitelistInFlatPropsIntoAbsolutePathsAndUpdateElement(
      newControl.options.sectionOptions,
      newControl.slug
    );

    this.turnRelativeDynamicPropPathsIntoAbsoluteAndUpdateElement(
      newControl.options.sectionOptions,
      newControl.slug
    );
  }

  get shouldControlValueBeAddedInAllBreakpoints() {
    const propertyPathsToWhitelistInFlatProps = this.context.element()
      .propertyPathsToWhitelistInFlatProps;

    return (
      propertyPathsToWhitelistInFlatProps &&
      propertyPathsToWhitelistInFlatProps.includes(
        this.twigPathWithoutRepeaterIndex
      )
    );
  }

  updatePropertyPathsToWhitelistInFlatProps(enable: boolean) {
    let newPropertyPathsToWhitelistInFlatProps = defensiveCopy(
      this.context.element().propertyPathsToWhitelistInFlatProps
    );

    if (enable) {
      if (!newPropertyPathsToWhitelistInFlatProps) {
        newPropertyPathsToWhitelistInFlatProps = [];
      }

      newPropertyPathsToWhitelistInFlatProps.push(
        this.twigPathWithoutRepeaterIndex
      );
    } else if (newPropertyPathsToWhitelistInFlatProps) {
      const propertyPathIndex = newPropertyPathsToWhitelistInFlatProps.findIndex(
        (path: string) => path === this.twigPathWithoutRepeaterIndex
      );

      if (propertyPathIndex > -1) {
        newPropertyPathsToWhitelistInFlatProps.splice(propertyPathIndex, 1);
      }
    }

    this.context.updateElementValue(
      "propertyPathsToWhitelistInFlatProps",
      newPropertyPathsToWhitelistInFlatProps
    );
  }

  get hasSsr() {
    return this.context.element().template.includes("%%SSR%%");
  }

  get shouldControlValueCauseSsr() {
    const propertyPathsToSsrElementWhenValueChanges = this.context.element()
      .propertyPathsToSsrElementWhenValueChanges;

    return (
      propertyPathsToSsrElementWhenValueChanges &&
      propertyPathsToSsrElementWhenValueChanges.includes(
        this.twigPathWithoutRepeaterIndex
      )
    );
  }

  updatePropertyPathsToSsrElementWhenValueChanges(enable: boolean) {
    let newPropertyPathsToSsrElementWhenValueChanges = defensiveCopy(
      this.context.element().propertyPathsToSsrElementWhenValueChanges
    );

    if (enable) {
      if (!newPropertyPathsToSsrElementWhenValueChanges) {
        newPropertyPathsToSsrElementWhenValueChanges = [];
      }

      newPropertyPathsToSsrElementWhenValueChanges.push(
        this.twigPathWithoutRepeaterIndex
      );
    } else if (newPropertyPathsToSsrElementWhenValueChanges) {
      newPropertyPathsToSsrElementWhenValueChanges = newPropertyPathsToSsrElementWhenValueChanges.filter(
        (path: string) => path !== this.twigPathWithoutRepeaterIndex
      );
    }

    this.context.updateElementValue(
      "propertyPathsToSsrElementWhenValueChanges",
      newPropertyPathsToSsrElementWhenValueChanges
    );
  }
}
